// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Control } from '../Control';
import CanvasRactiveTemplate from '@front/templates/CanvasRactiveTemplate.html';
export class CanvasRactive extends Control {
    constructor() {
        super();
    }
    getType() {
        return 'CanvasRactive';
    }
    // #region Ractive lifecycle
    oninit() {
        super.oninit();
        CanvasRactive.staticId++;
        void this.set('id', 'canvas-id-' + CanvasRactive.staticId);
    }
    oncomplete() {
        super.oncomplete();
        this.subscribe();
    }
    onteardown() {
        this.unsubscribe();
        super.onteardown();
    }
    onrender() {
        const canvas = this.getCanvas();
        const ctx = canvas.getContext('2d', { alpha: true });
        if (ctx !== null) {
            this._canvasContext = ctx;
        }
        this.themeChanged();
    }
    themeChanged() {
        if (!isNullOrUndefined(this._drawer)) {
            this._drawer.ThemeChanged();
            this._drawer.setIsDirty();
        }
    }
    // #endregion
    // #region Virtual functions
    setDrawer(drawer) {
        this._drawer = drawer;
    }
    getDrawer() {
        return this._drawer;
    }
    tickAsync() {
        if (isNullOrUndefined(this._drawer))
            return;
        if (!this._drawer.isDirty())
            return;
        if (isNullOrUndefined(this._canvasContext))
            return;
        this._drawer.Draw(this._canvasContext);
        this._drawer.setIsNotDirty();
    }
    // #endregion
    // #region Eventhandlers
    onResize() {
        const canvas = this.getCanvas();
        if (isNullOrUndefined(canvas)) {
            return;
        }
        const parentDiv = canvas.parentElement;
        if (isNullOrUndefined(parentDiv)) {
            return;
        }
        const devicePixelRatio = window.devicePixelRatio ? window.devicePixelRatio : 1;
        const width = parentDiv.clientWidth;
        const height = parentDiv.clientHeight;
        canvas.width = width * devicePixelRatio;
        canvas.height = height * devicePixelRatio;
        this._canvasContext.scale(devicePixelRatio, devicePixelRatio);
        this._drawer.onSizeChanged(width, height);
        this._drawer.setIsDirty();
    }
    onMouseDown(event) {
        super.onMouseDown(event);
        if (!isNullOrUndefined(this._drawer)) {
            this._drawer.onMouseDown(event.original);
            this._drawer.setIsDirty();
        }
    }
    onMouseUp(event) {
        super.onMouseUp(event);
        if (!isNullOrUndefined(this._drawer)) {
            this._drawer.onMouseUp(event.original);
            this._drawer.setIsDirty();
        }
    }
    onMouseMove(event) {
        super.onMouseMove(event);
        if (!isNullOrUndefined(this._drawer)) {
            this._drawer.onMouseMove(event.original);
            const currentCursor = this._drawer.getCursor();
            void this.set('currentCursor', currentCursor);
            this._drawer.setIsDirty();
        }
    }
    onMouseLeave(event) {
        super.onMouseLeave(event);
        if (!isNullOrUndefined(this._drawer)) {
            this._drawer.onMouseLeave(event.original);
            this._drawer.setIsDirty();
        }
    }
    onClick(event) {
        super.onClick(event);
        if (!isNullOrUndefined(this._drawer)) {
            this._drawer.onClick(event.original);
            this._drawer.setIsDirty();
        }
    }
    // #endregion
    // #region Technical functions
    subscribe() {
        Control.Ticker.Subscribe(this.tickAsync, this);
        this._observer = new ResizeObserver(() => {
            this.onResize();
        });
        const canvas = this.getCanvas();
        if (!isNullOrUndefined(canvas)) {
            this._observer.observe(canvas);
        }
    }
    unsubscribe() {
        Control.Ticker.UnSubscribe(this.tickAsync, this);
        const canvas = this.getCanvas();
        if (!isNullOrUndefined(canvas)) {
            this._observer.unobserve(canvas);
        }
    }
    getCanvas() {
        const elementId = super.get('id');
        try {
            const element = super.find('#' + elementId);
            return element;
        }
        catch (e) {
            return null;
        }
    }
}
CanvasRactive.staticId = 0;
Control.extendWith(CanvasRactive, {
    template: CanvasRactiveTemplate,
    data: function () {
        return {
            id: 'canvas-id',
            currentCursor: 'default'
        };
    }
});
